/**
 * @fileoverview Chat-header options menu for live eligible conversations.
 * @module components/ChatHeaderMenu
 */

import { OptionsDropdown } from './OptionsDropdown.js';
import { API } from '../core/api.js';
import { sessionManager } from '../core/SessionManager.js';
import { eventBus } from '../core/utils.js';
import { modalService } from '../services/ModalService.js';
import { emitContentDeleted } from '../core/contentSync.js';
import { Tooltip } from '../utils/Tooltip.js';
import page from '../vendor/page.js';

export class ChatHeaderMenu {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.container - Header mount
   * @param {Object} options.privacyModeManager - Privacy state/actions
   * @param {Object} options.modeOrchestrator - Live chat teardown owner
   * @param {Object} options.app - App instance holding returnSession
   * @param {Object} options.featureToggleService - Toggle service
   */
  constructor({ container, privacyModeManager, modeOrchestrator, app, featureToggleService }) {
    this.container = container;
    this.privacyModeManager = privacyModeManager;
    this.modeOrchestrator = modeOrchestrator;
    this.app = app;
    this.featureToggleService = featureToggleService;
    this.privacyVisible = featureToggleService?.get('privacyButton') === true;
    this._actionInFlight = false;

    this.dropdown = container
      ? new OptionsDropdown({
          container,
          items: () => this._items(),
          onAction: (action) => this._handleAction(action),
          buttonText: 'Conversation options'
        })
      : null;

    // Tooltip for the icon-only kebab. Wired here (not in the shared
    // OptionsDropdown component) so it stays scoped to this header menu, and
    // placed to the left because the button sits at the header's right edge —
    // "below" would drop over the first message of the conversation.
    this._tooltipCleanup = this.dropdown?.buttonElement
      ? Tooltip.setup(this.dropdown.buttonElement, {
          position: 'left',
          getText: () => 'Chat options'
        })
      : null;

    eventBus.on('session:started', ({ starred }) => {
      sessionManager.setCurrentStarred(starred === true);
    });
    eventBus.on('toggle:privacyButton', ({ visible }) => {
      this.privacyVisible = visible === true;
    });
    eventBus.on('toggle:headerOptionsMenu', ({ visible }) => this._applyVisibility(visible));
    eventBus.on('privacy:changed', () => this.dropdown?.close());
    this._applyVisibility(featureToggleService?.get('headerOptionsMenu') === true);
  }

  /** @returns {Array<Object>} Current menu items. */
  _items() {
    const privateMode = this.privacyModeManager?.getIsPrivacyMode() === true;
    if (privateMode) {
      return [
        { action: 'share', label: 'Share conversation' },
        { action: 'privacy', label: 'End Privacy Mode', icon: 'lock-locked' }
      ];
    }

    const starred = sessionManager.getCurrentStarred();
    const items = [{ action: 'end_session', label: 'End chat session' }];
    items.push({ action: 'share', label: 'Share conversation' });
    if (this.privacyVisible) {
      items.push({ action: 'privacy', label: 'Enter Privacy Mode', icon: 'lock-unlocked' });
    }
    items.push({
      action: 'star',
      label: starred ? 'Unstar' : 'Star',
      icon: starred ? 'star' : 'star-outline'
    });
    items.push({ action: 'delete', label: 'Delete', variant: 'danger' });
    return items;
  }

  /** @param {boolean} visible Apply the dedicated feature toggle. */
  _applyVisibility(visible) {
    this.container?.classList.toggle('feature-toggle--hidden', visible !== true);
  }

  /** @returns {string|null} Active session id, warning when absent. */
  _sessionId() {
    const sessionId = sessionManager.getCurrentSessionId();
    if (!sessionId) console.warn('[ChatHeaderMenu] Action ignored without an active session');
    return sessionId;
  }

  /** @param {string} action Dispatch a menu action. */
  async _handleAction(action) {
    if (this._actionInFlight) return;
    const sessionId = this._sessionId();
    if (!sessionId) return;

    if (action === 'share') {
      window.notificationManager?.showInfo?.('Sharing is coming soon');
      return;
    }
    if (action === 'privacy') {
      if (this.privacyModeManager.getIsPrivacyMode()) {
        await this.privacyModeManager.requestExit();
      } else {
        await this.privacyModeManager.enterPrivacyMode();
      }
      return;
    }

    this._actionInFlight = true;
    try {
      if (action === 'star') await this._toggleStar(sessionId);
      if (action === 'end_session') await this._endSession();
      if (action === 'delete') await this._delete(sessionId);
    } finally {
      this._actionInFlight = false;
    }
  }

  /** @param {string} sessionId Optimistically toggle and persist the star. */
  async _toggleStar(sessionId) {
    const previous = sessionManager.getCurrentStarred();
    const next = !previous;
    sessionManager.setCurrentStarred(next);
    try {
      const result = await API.starSession(sessionId, next);
      if (result && result.success !== false && result.starred === next) return;
    } catch {
      // The optimistic state is rolled back below for transport and API errors.
    }

    if (sessionManager.getCurrentSessionId() === sessionId) {
      sessionManager.setCurrentStarred(previous);
    }
    window.notificationManager?.showError?.("Couldn't update this conversation — please try again.");
  }

  /** Confirm, close the session, then return to the dashboard. */
  async _endSession() {
    const confirmed = await modalService.confirm({
      title: 'End chat session',
      message: 'This will close your current conversation. You can start a new one anytime.',
      confirmText: 'End Session',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    const closed = await sessionManager.endSession();
    if (!closed) {
      window.notificationManager?.showError?.("Couldn't end this session — please try again.");
      return;
    }

    this.app.returnSession = null;
    this.modeOrchestrator.returnToDashboard();
    page('/');
  }

  /** @param {string} sessionId Confirm, delete, then run the ratified teardown sequence. */
  async _delete(sessionId) {
    const confirmed = await modalService.confirm({
      title: 'Delete conversation',
      message: 'This conversation will be removed. This can’t be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed) return;

    const result = await API.deleteSession(sessionId);
    if (!result?.deleted) {
      window.notificationManager?.showError?.("Couldn't delete this conversation — please try again.");
      return;
    }

    const sessionNoteId = sessionManager.getCurrentSessionNoteId();
    this.app.returnSession = null;
    this.modeOrchestrator.returnToDashboard();
    eventBus.emit('session:deleted', { sessionId });
    if (sessionNoteId) emitContentDeleted('session', sessionNoteId);
    page('/');
  }

  /** Release the tooltip listeners and the dropdown DOM. */
  destroy() {
    this._tooltipCleanup?.();
    this._tooltipCleanup = null;
    this.dropdown?.destroy();
  }
}
